import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "@adobe/css-tools";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonPosition = "buttonPosition",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "customViewport" | "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.buttonPosition]: "left" | "center" | "right";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.customCSS]: string;
    };
    extendedStyles?: BannerStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.buttonLink);
    return {
        url: link?.url,
        title: link?.title,
    };
};

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    bannerButton?: LinkPresentationProps;
    bannerButtonContainer?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
    bannerButtonContainer: {
        css: css`
            justify-content: flex-end;
            margin-top: auto;
        `,
    },
    bannerButton: {
        css: css`
            cursor: pointer;
            line-height: inherit;
            word-break: keep-all;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            min-height: 40px;
            transition: 200ms ease-in-out;
            &:hover {
                background: rgb(58, 129, 74);
                border-color: rgb(58, 129, 74);
                span {
                    color: #141414 !important;
                }
            }
            span {
                color: #141414 !important;
                font-size: 12px;
                font-weight: bold;
                font-family: inherit;
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export const Banner: React.FC<Props> = ({ fields, url, title, history, extendedStyles }) => {
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.image).url});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "customViewport":
            minimumHeightStyles = "max-height: 20vh;";
            break;
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 40vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    let buttonPositionStyles;
    switch (fields.buttonPosition) {
        case "left":
            buttonPositionStyles = "margin-right: 80%";
            break;
        case "center":
            buttonPositionStyles = "margin: 0;";
            break;
        case "right":
            buttonPositionStyles = "margin-left: 80%";
            break;
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };
    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding}px;
        `,
    };

    const buttonStyles = {
        css: css`
            ${styles.bannerButton?.css || ""}
            ${buttonPositionStyles}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles}>
                        <Typography className="banner-heading">{parse(fields.heading, parserOptions)}</Typography>
                        <Typography className="banner-subheading">{parse(fields.subheading, parserOptions)}</Typography>
                        <StyledWrapper {...styles.bannerButtonContainer}>
                            {!fields.disableButton && (
                                <Link {...buttonStyles} href={url} data-test-selector="banner-button">
                                    {fields.buttonLabel || title || url}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};
const banner: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(Banner)),
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                fieldType: "Contextual",
                name: fields.buttonPosition,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                defaultValue: "right",
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Custom Viewport", value: "customViewport" },
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
